import { useQuery } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { CirclePlay } from 'lucide-react'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/elements/accordion'
import { Skeleton } from '@/components/elements/skeleton'
import { getFaqs } from '@/services/faq.service'

/**
 * Read-only FAQ accordion (RSCX-353). FAQs are RescueX-curated platform
 * guidance, shown to every account from the Documents area.
 */
export const FaqList = () => {
  const { data: faqs, isPending } = useQuery({
    queryKey: ['faqs'],
    queryFn: getFaqs,
  })

  if (isPending) {
    return <Skeleton className="h-32 w-full" />
  }

  const entries = faqs?.results ?? faqs ?? []

  if (entries.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No FAQs have been published yet. Check back soon!
      </p>
    )
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      {entries.map((faq) => {
        const videoHref = faq.videoUrl || faq.signedVideoPath
        return (
          <AccordionItem key={faq.id} value={faq.id}>
            <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
            <AccordionContent>
              <div
                className="prose prose-sm text-foreground max-w-none"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }}
              />
              {videoHref && (
                <a
                  href={videoHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary mt-3 flex w-fit flex-row items-center gap-1 text-sm font-medium hover:underline"
                >
                  <CirclePlay className="h-4 w-4" />
                  Watch video
                </a>
              )}
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}
